import type { Observable } from 'rxjs';

import { RequestType } from '../enum';
import type { RequestFeedback } from '../models';
import { RequestFacadeModel, RequestModel } from '../models';
import type { BackendService } from '../services/backend.service';
import type { ID } from '../types';

/**
 * Base Api Service with common used methods, created to DRY
 *
 * @export
 * @class BaseApiService
 */
export abstract class BaseApiService {
  /**
   * Used to decrease amount of repeatable URL typings
   *
   * @private
   * @type {string}
   * @memberof BaseApiService
   */
  private readonly apiExtension: string;

  /**
   * Creates an instance of BaseApiService.
   *
   * @param backendService
   * @param apiExtension - REST endpoint
   * @memberof BaseApiService
   */
  protected constructor(
    protected readonly backendService: BackendService,
    apiExtension: string,
  ) {
    this.apiExtension = apiExtension;
  }

  /**
   * Since extension is private here's getter for it
   *
   * @return string
   * @memberof BaseApiService
   */
  public extension(): string {
    return this.apiExtension;
  }

  /**
   * Get all entity
   *
   * @template TYPE - Return Type
   * @return
   * @memberof BaseApiService
   * @param customUrl
   * @param params
   */
  protected loadAll<TYPE>(customUrl?: string, params?: any): Observable<TYPE> {
    const request: RequestModel = new RequestModel<null>({
      url: this.getFullUrl(),
      skipNotify: true,
    });
    if (customUrl) {
      request.url = '';
      request.customUrl = customUrl;
    }
    if (params) {
      request.withQuery(params);
    }
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<null>({
      requestType: RequestType.get,
      request,
    });
    return this.send<TYPE, null>(requestFacade);
  }

  protected getEntity<TYPE>(id: ID): Observable<TYPE> {
    const request: RequestModel = new RequestModel<null>({
      url: this.getFullUrl(),
    }).withID(id);
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<null>({
      requestType: RequestType.get,
      request,
    });
    return this.send<TYPE, null>(requestFacade);
  }
  /**
   * Update entity by ID
   *
   * @template TYPE - Return Type
   * @return
   * @memberof BaseApiService
   * @param id
   * @param body
   * @param requestType
   * @param successMessage
   */
  protected updateById<TYPE>(
    id: ID,
    body: any,
    requestType = RequestType.put,
    successMessage?: RequestFeedback,
  ): Observable<TYPE> {
    const request: RequestModel<any> = new RequestModel<any>({
      url: this.getFullUrl(),
      requestBody: body,
      successMessage,
    }).withID(id);
    const requestFacade: RequestFacadeModel<any> = new RequestFacadeModel<any>({
      requestType,
      request,
    });
    return this.send<TYPE, any>(requestFacade);
  }
  /**
   * Delete entity by ID
   *
   * @template TYPE - Return Type
   * @return
   * @memberof BaseApiService
   * @param id
   * @param successMessage
   */
  protected deleteEntity<TYPE>(id?: ID, successMessage?: RequestFeedback): Observable<TYPE> {
    const request: RequestModel = new RequestModel<null>({
      url: this.getFullUrl(),
      successMessage,
    });
    if (id) {
      request.withID(id);
    }
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<null>({
      requestType: RequestType.delete,
      request,
    });
    return this.send<TYPE, null>(requestFacade);
  }

  /**
   * Combines apiExtension
   *
   * @param url
   * @param extension
   * @return
   * @memberof BaseApiService
   */
  protected getFullUrl(url: string | null = null, extension = this.apiExtension): string {
    return url ? `${extension}/${url}/` : `${extension}/`;
  }

  /**
   * Send Request with necessary types
   *
   * @template T-Return Type
   * @template R-DTO Type
   * @param requestFacade
   * @return
   * @memberof BaseApiService
   */
  protected send<T, R = null>(requestFacade: RequestFacadeModel<R>): Observable<T> {
    return this.backendService.send<T, R>(requestFacade);
  }
}
